import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { activateNewPassword, recoveryPassword } from '../../../../stores/auth.store';
import { getAuthElemStyle, isEmail, isPassword, setFocus } from '../../../../common/tools';
import { useTranslation } from 'react-i18next';
import React, { useContext, useEffect, useState } from 'react';
import { ArrowBackSvg, CrossedOutEyeSvg, EyeSvg } from '../../../../icons';
import { MainContext } from '../../../../context/main.context';
import { ISuccessfulAuth } from '../../../../interfaces';
import VerificationCode from '../verification-code';
import { ACTIVATE_NEW_PASSWORD_ERROR, RECOVERY_PASSWORD_ERROR } from '../../../../common/errorCodes';
import Header from '../../../header';
import Recaptcha from '../../recaptcha';
import Footer from '../../../footer';
import ManualController from '../../../../hoc/ManualController';
import LoginByMail from '../login';


export default function RecoveryPassword() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [ email , setEmail ] = useState<string>('');
	const [ showVerificationCodeComponent, setShowVerificationCodeComponent ] = useState<boolean>(false);
	const [ showChangePasswordMenu, setShowChangePasswordMenu ] = useState<boolean>(false);
	const [ password, setPassword ] = useState<string>('');
	const [ repeatPassword, setRepeatPassword ] = useState<string>('');
	const { manualRedirect } = ManualController();
	const [ hidePassword, setHidePassword ] = useState<boolean>(true);
	const [ hideRepeatPassword, setHideRepeatPassword ] = useState<boolean>(true);
	const [ passwordErr, setPasswordErr ] = useState<string>('');
	const [ repeatPasswordErr, setRepeatPasswordErr ] = useState<string>('');
	const [ isDisableBtn, setDisableBtn ] = useState<boolean>(true);

	const {
		showCaptcha,
		settings,
		modalContent,
		setModalContent,
		session,
		source,
		locale,
		project,
		failedAttemptCenter,
		toggleLoader,
	} = useContext(MainContext);

	useEffect(() => {
		if (password && repeatPassword && !showCaptcha) {
			setDisableBtn(false);
		} else {
			setDisableBtn(true);
		}
	}, [ password, repeatPassword, showCaptcha ]);

	const _navigate = (path: string): void => {
		if (session) {
			navigate({
				pathname: path,
				search: `?session=${session}&source=${source}&locale=${locale}&project=${project}`,
			});
		} else {
			navigate(path);
		}
	}

	const _onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
		e.preventDefault();

		if (!email) {
			setFocus('rec-pass-email-id');
			toast.warning(t('login.enterEmail'));
			return;
		}

		if (!isEmail(email)) {
			toast.warning(t('login.invalidEmail'));
			return;
		}

		_recoveryPassword();
	}

	const _failedAttemptCenter = (): void => {
		if (failedAttemptCenter) {
			failedAttemptCenter('add');
		}
	}

	const _recoveryPassword = (): void => {
		toggleLoader(true);

		recoveryPassword(email).then((res: { data: ISuccessfulAuth }): void => {
			const warningMessage: string | undefined = res?.data?.warningMessage;

			if (warningMessage === 'USER_NOT_EXIST_RECOVERY_PASSWORD') {
				toast.warning(t('login.userNotExists'));
				_failedAttemptCenter();
				return;
			}

			const result: string | undefined = res?.data.result;

			if (result) {
				setShowVerificationCodeComponent(true);
				return;
			}

			manualRedirect(res, 'auth', true, RECOVERY_PASSWORD_ERROR);
		}, err => {
			const errData = err?.response?.data || err;

			manualRedirect(errData, 'auth', true, RECOVERY_PASSWORD_ERROR);
		}).finally((): void => {
			toggleLoader(false);
		});
	}

	const _activateNewPassword = (): void => {
		toggleLoader(true);

		activateNewPassword(email, password).then((res: { data: ISuccessfulAuth }): void => {
			const warningMessage: string | undefined = res?.data?.warningMessage;

			if (warningMessage === 'PASSWORD_EXIST_ACTIVATE_NEW_PASSWORD') {
				toast.warning(t('login.passwordUsedBefore'));

				_failedAttemptCenter();

				if (modalContent) {
					setModalContent({ component: <LoginByMail/> });
					return;
				}

				_navigate('/session/login/email');
				return;
			}

			const result: string|undefined = res?.data.result;

			if (result) {
				toast.success(t('login.passwordUpdatedSuccessfully'));

				if (modalContent) {
					setModalContent({ component: <LoginByMail/> });
					return;
				}

				_navigate('/session/login/email');
				return;
			}

			manualRedirect(res, 'auth', true, ACTIVATE_NEW_PASSWORD_ERROR);
		}, err => {
			const errData = err?.response?.data || err;

			manualRedirect(errData, 'auth', true, ACTIVATE_NEW_PASSWORD_ERROR);
		}).finally((): void => {
			toggleLoader(false);
		});
	}

	const _onSubmitNewPassword = (e: React.FormEvent<HTMLFormElement>): void => {
		e.preventDefault();

		if (!password) {
			setFocus('rec-mew-pass-id');
			setPasswordErr(t('login.enterPassword'));
			return;
		}

		if (!isPassword(password)) {
			setFocus('rec-mew-pass-id');
			setPasswordErr(t('login.passwordValidation'));
			return;
		}

		if (passwordErr) {
			setPasswordErr('');
		}

		if (!repeatPassword) {
			setFocus('rec-mew-pass-rep-id');
			setRepeatPasswordErr(t('login.enterPasswordAgain'));
			return;
		}

		if (!isPassword(repeatPassword)) {
			setFocus('rec-mew-pass-rep-id');
			setRepeatPasswordErr(t('login.passwordValidation'));
			return;
		}

		if (password !== repeatPassword) {
			setPasswordErr('');
			setRepeatPasswordErr(t('login.mismatchPassword'));
			return;
		}

		if (repeatPasswordErr) {
			setRepeatPasswordErr('');
		}

		_activateNewPassword();
	}

	const _setShowChangePasswordMenu = (): void => {
		setShowVerificationCodeComponent(false);
		setShowChangePasswordMenu(true);
	}

	const _renderMenu = () => {
		return (
			<div className="auth-modal-menu">
				<div className="login-modal-header">
					<div
						className="login-modal-header-prev"
						onClick={() => {
							if (modalContent) {
								setModalContent({ component: <LoginByMail/> });
								return;
							}

							_navigate('/session/login/email');
						}}
					>
						<ArrowBackSvg/>
						{t('common.back')}
					</div>
					<div className="login-modal-header-title text-overflow">
						{t('login.passwordRecovery')}
					</div>
				</div>
				<div className="recovery-password-header-sub-title text-overflow">
					{t('login.passwordRecoveryTitle')}
				</div>
				<form
					noValidate
					className="auth-modal-menu-body"
					onSubmit={(e) => _onSubmit(e)}
				>
					<div className="login-modal-menu-body-input">
						<div className="login-modal-menu-body-input-title">
							{t('login.emailLabel')}
						</div>
						<input
							className="login-modal-menu-body-input-item"
							type="email"
							id="rec-pass-email-id"
							autoComplete="off"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						>
						</input>
					</div>
					<button className="btn" type="submit" disabled={showCaptcha}>
						{t('login.sendLetter')}
					</button>
					<div className="auth-modal-menu-footer">
						<div className="auth-modal-menu-footer-no-acc text-overflow">
							{t('login.rememberPassword')}?
						</div>
						<div
							className="auth-modal-menu-footer-reg text-overflow"
							onClick={() => {
								if (modalContent) {
									setModalContent({ component: <LoginByMail/> });
									return;
								}

								_navigate('/session/login/email');
							}}
						>
							{t('login.loginButton')}
						</div>
					</div>
				</form>
				<Recaptcha/>
			</div>
		);
	}

	const _renderMenuNewPassword = () => {
		return (
			<div className="auth-modal-menu">
				<div className="login-modal-header">
					<div
						className="login-modal-header-prev"
						onClick={() => {
							if (modalContent) {
								setModalContent({ component: <LoginByMail/> });
								return;
							}

							_navigate('/session/login/email');
						}}
					>
						<ArrowBackSvg/>
						{t('common.back')}
					</div>
					<div className="login-modal-header-title text-overflow">
						{t('login.newPassword')}
					</div>
				</div>
				<form
					noValidate
					className="auth-modal-menu-body req-auth-modal-menu-body"
					onSubmit={(e) => _onSubmitNewPassword(e)}
				>
					<div className="login-modal-menu-body-input">
						<div className="login-modal-menu-body-input-title">
							{t('login.passwordLabel')}
						</div>
						<div className="login-modal-menu-body-input-password">
							<input
								className="login-modal-menu-body-input-item login-password"
								id="rec-mew-pass-id"
								type={hidePassword ? 'password' : 'text'}
								autoComplete="off"
								title={t('login.passwordValidation')}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							>
							</input>
							<div className="password-hide" onClick={() => setHidePassword(!hidePassword)}>
								{hidePassword ? <EyeSvg/> : <CrossedOutEyeSvg/>}
							</div>
							{ passwordErr ? <div className="req-input-err">{passwordErr}</div> : null }
						</div>
					</div>
					<div className="login-modal-menu-body-input">
						<div className="login-modal-menu-body-input-title">
							{t('login.repeatPasswordLabel')}
						</div>
						<div className="login-modal-menu-body-input-password">
							<input
								className="login-modal-menu-body-input-item login-password"
								id="rec-mew-pass-rep-id"
								type={hideRepeatPassword ? 'password' : 'text'}
								autoComplete="off"
								title={t('login.passwordValidation')}
								value={repeatPassword}
								onChange={(e) => setRepeatPassword(e.target.value)}
							>
							</input>
							<div className="password-hide" onClick={() => setHideRepeatPassword(!hideRepeatPassword)}>
								{hideRepeatPassword ? <EyeSvg/> : <CrossedOutEyeSvg/>}
							</div>
							{ repeatPasswordErr ? <div className="req-input-err">{repeatPasswordErr}</div> : null }
						</div>
					</div>
					<button
						className="btn"
						type="submit"
						disabled={isDisableBtn}
						style={{
							background: isDisableBtn ? '#D1D1D0' : '#2368a2',
							cursor: isDisableBtn ? 'auto' : 'pointer',
						}}
					>
						{t('login.confirmation')}
					</button>
				</form>
				<Recaptcha/>
			</div>
		);
	}

	return (
		<div className="auth" id="auth-element-id" style={{background: !modalContent ? '#e8e8e8' : 'unset'}}>
			<div
				className="auth-container"
				style={{
					background: !modalContent ? '#e8e8e8' : 'unset',
					minHeight: !modalContent ? 'calc(100vh - 100px)' : '100%',
				}}
			>
				<div className="auth-modal" style={getAuthElemStyle(settings)}>
					<Header origin="auth"/>
					{ !showVerificationCodeComponent && !showChangePasswordMenu ? _renderMenu() : null }
					{
						showVerificationCodeComponent ?
							<VerificationCode
								type='recoveryPassword'
								email={email}
								onClose={() => setShowVerificationCodeComponent(false)}
								onResend={() => _recoveryPassword()}
								onChangePassword={() => _setShowChangePasswordMenu()}
							/>
						: null
					}
					{ showChangePasswordMenu ? _renderMenuNewPassword() : null }
				</div>
			</div>
			{!modalContent && <Footer/>}
		</div>
	);
}

